<template>
  <!-- 1. Allowed: render the action button with whatever the parent passed. -->
  <button
    v-if="allowed"
    type="button"
    v-bind="$attrs"
    :class="buttonClass"
    :disabled="disabled"
    @click="onClick"
  >
    <slot>
      <i v-if="icon" :class="icon"></i>
      <span v-if="label">{{ label }}</span>
    </slot>
  </button>

  <!-- 2. Denied but requestable: render the "Request action" button + modal. -->
  <template v-else-if="requestable && entityId">
    <button
      type="button"
      :class="['permission-btn-request', requestClass]"
      :disabled="disabled"
      @click="modalOpen = true"
      :title="`Request approval to ${actionLabel.toLowerCase()}`"
    >
      <i :class="requestIcon || 'fas fa-paper-plane'"></i>
      <span>{{ requestLabel || `Request ${actionLabel.toLowerCase()}` }}</span>
    </button>
    <RequestActionModal
      v-model:open="modalOpen"
      :module="module"
      :action="action"
      :action-type="actionType"
      :action-label="actionLabel"
      :entity-type="entityType"
      :entity-id="entityId"
      :house-id="houseId"
      :payload="payload"
      @submitted="$emit('requested', $event)"
    />
  </template>

  <!-- 3. Denied and not requestable (or no entity): render nothing. -->
</template>

<script>
import RequestActionModal from "./RequestActionModal.vue";

/**
 * Permission-aware button. Replaces the pattern of
 *    <button v-if="$access('Module','action')">…</button>
 *    <button v-else @click="openRequestModal">Request …</button>
 *
 * with:
 *    <PermissionButton module="Tenants" action="delete"
 *      entity-type="tenant" :entity-id="tenant.tenant_id"
 *      :payload="{ tenant_name: tenant.tenant_name }"
 *      @click="deleteTenant(tenant)">
 *      <i class="fas fa-trash"></i><span>Delete</span>
 *    </PermissionButton>
 *
 * If the user has the permission, the slot renders inside a <button> that
 * emits @click. If not, and `requestable` is true (default), a "Request"
 * button + RequestActionModal is rendered instead. Submitting the request
 * posts to /v1/adminActionRequests/create with action_type = `${module}:${action}`
 * (or an explicit `actionType` override for legacy enum values like 'delete_tenant').
 *
 * Use `requestable=false` when there is no entity yet (e.g. a top-level "Add"
 * button) — in that case the button is simply hidden when permission is denied.
 */
export default {
  name: "PermissionButton",
  components: { RequestActionModal },
  inheritAttrs: false,
  props: {
    module: { type: String, required: true },
    action: { type: String, required: true },

    // Action-type override (use a legacy enum, e.g. "delete_tenant", instead of
    // the default `${module}:${action}` form). Only matters when requestable.
    actionType: { type: String, default: null },

    // Human label used in the modal & on the request button: "Request <label>".
    // Falls back to the action verb if not given.
    actionLabel: { type: String, default: "" },

    // Required if requestable: the entity this action targets.
    entityType: { type: String, default: "" },
    entityId: { type: [String, Number], default: null },
    houseId: { type: [String, Number], default: null },
    payload: { type: Object, default: () => ({}) },

    // UX
    label: { type: String, default: "" },
    icon: { type: String, default: "" },
    buttonClass: { type: [String, Array, Object], default: "" },
    requestClass: { type: [String, Array, Object], default: "" },
    requestLabel: { type: String, default: "" },
    requestIcon: { type: String, default: "" },
    disabled: { type: Boolean, default: false },

    // If true (default), denied users see a "Request" button + modal.
    // Set false for actions that have no entity yet (top-level "Add").
    requestable: { type: Boolean, default: true },
  },
  emits: ["click", "requested"],
  data() {
    return { modalOpen: false };
  },
  computed: {
    allowed() {
      return this.$access(this.module, this.action);
    },
  },
  methods: {
    onClick(event) {
      this.$emit("click", event);
    },
  },
};
</script>

<style scoped>
.permission-btn-request {
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  padding: 0.4rem 0.75rem;
  background: #f1f5f9;
  color: #475569;
  border: 1px solid #cbd5e1;
  border-radius: 6px;
  font-size: 0.85rem;
  cursor: pointer;
  transition: background 0.15s ease, color 0.15s ease, border-color 0.15s ease;
}
.permission-btn-request:hover:not(:disabled) {
  background: #e2e8f0;
  color: #1e293b;
  border-color: #94a3b8;
}
.permission-btn-request:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}
.permission-btn-request i {
  font-size: 0.85rem;
}
</style>
